/** Thin typed fetch client for the API Worker. Cookies carry the session. */

import type { CvSections, ParsedResume } from "@epicredible/shared";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const message =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export interface Membership {
  issuerId: string;
  issuerSlug: string;
  issuerName: string;
  role: string;
}
export interface Me {
  user: { userId: string; email: string };
  memberships: Membership[];
}
export interface Workspace {
  id: string;
  name: string;
  slug: string;
}
export interface Group {
  id: string;
  name: string;
  cohort: string | null;
}
export interface Template {
  id: string;
  name: string;
  type: string;
  canvas: Record<string, unknown>;
  fields: Record<string, unknown>;
}
export interface IssuanceJob {
  id: string;
  status: string;
  total: number;
  processed: number;
  failed: number;
}
export interface WalletCredential {
  publicId: string;
  recipientName: string;
  status: string;
  issuedAt: string;
  obJson: unknown;
}
export interface Resume {
  id: string;
  fileName: string | null;
  mime: string;
  status: "uploaded" | "parsing" | "parsed" | "edited" | "failed";
  parsed: ParsedResume | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface SkillClaim {
  id: string;
  rawText: string;
  source: string;
  verified: boolean;
  confidence: number | null;
  canonicalId: string | null;
  label: string | null;
  proofCredentialId: string | null;
}
export interface SkillRecommendation {
  canonicalId: string;
  label: string;
  taxonomyRef: string;
  confidence: number;
}
export interface ExternalVerificationResult {
  ok: boolean;
  provider: string;
  url: string;
  title: string | null;
  issuer: string | null;
  recipient: string | null;
  issuedOn: string | null;
  error?: string;
}
export interface CvDocument {
  id: string;
  title: string;
  slug: string;
  sections: CvSections;
  isSearchableByEmployers: boolean;
  publishedAt: string | null;
  pdfKey: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Public verify base (the SSR Worker). Overridable at build via Vite env. */
export const PUBLIC_BASE_URL =
  (import.meta.env.VITE_PUBLIC_BASE_URL as string | undefined) ?? "http://localhost:8788";

export const api = {
  // auth
  requestMagicLink: (email: string) =>
    request<{ ok: boolean; devMagicLink?: string; devToken?: string }>("/v1/auth/magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  verify: (token: string) =>
    request<{ user: { id: string; email: string } }>("/v1/auth/verify", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  logout: () => request<{ ok: boolean }>("/v1/auth/logout", { method: "POST" }),
  me: () => request<Me>("/v1/auth/me"),

  // issuers
  createIssuer: (name: string, slug: string) =>
    request<{ issuer: { id: string; slug: string } }>("/v1/issuers", {
      method: "POST",
      body: JSON.stringify({ name, slug }),
    }),

  // tenancy
  listWorkspaces: (slug: string) =>
    request<{ workspaces: Workspace[] }>(`/v1/issuers/${slug}/workspaces`),
  createWorkspace: (slug: string, name: string, wsSlug: string) =>
    request<{ workspace: Workspace }>(`/v1/issuers/${slug}/workspaces`, {
      method: "POST",
      body: JSON.stringify({ name, slug: wsSlug }),
    }),
  listGroups: (slug: string, wsId: string) =>
    request<{ groups: Group[] }>(`/v1/issuers/${slug}/workspaces/${wsId}/groups`),
  createGroup: (slug: string, wsId: string, name: string, cohort?: string) =>
    request<{ group: Group }>(`/v1/issuers/${slug}/workspaces/${wsId}/groups`, {
      method: "POST",
      body: JSON.stringify({ name, cohort }),
    }),
  listTemplates: (slug: string, wsId: string) =>
    request<{ templates: Template[] }>(`/v1/issuers/${slug}/workspaces/${wsId}/templates`),
  saveTemplate: (
    slug: string,
    wsId: string,
    body: {
      id?: string;
      name: string;
      type?: string;
      canvas?: Record<string, unknown>;
      fields?: Record<string, unknown>;
    },
  ) =>
    request<{ template: Template }>(`/v1/issuers/${slug}/workspaces/${wsId}/templates`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // bulk issuance
  bulkIssue: (
    slug: string,
    body: { groupId: string; achievement: { name: string; criteria?: string }; csv?: string; recipients?: unknown[] },
  ) =>
    request<{ jobId: string; issued?: number; queued?: number; skipped?: number; mode: string }>(
      `/v1/issuers/${slug}/issuance/bulk`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  getJob: (slug: string, jobId: string) =>
    request<{ job: IssuanceJob }>(`/v1/issuers/${slug}/issuance/jobs/${jobId}`),

  // claim + wallet + sharing (M3)
  redeemClaim: (token: string) =>
    request<{ credential: { publicId: string; status: string }; user: { id: string; email: string } }>(
      "/v1/claims/redeem",
      { method: "POST", body: JSON.stringify({ token }) },
    ),
  getWallet: () => request<{ credentials: WalletCredential[] }>("/v1/wallet"),
  recordShare: (publicId: string, channel: string) =>
    request<{ ok: boolean }>(`/v1/credentials/${publicId}/share`, {
      method: "POST",
      body: JSON.stringify({ channel }),
    }),

  // analytics (M3, REQ-1.4)
  getAnalytics: (slug: string) =>
    request<{
      totals: {
        issued: number;
        claimed: number;
        claimRate: number;
        shares: number;
        sharesByChannel: Record<string, number>;
        opens: number | null;
      };
      history: { day: string; issued: number; claimed: number; shares: number }[];
    }>(`/v1/issuers/${slug}/analytics`),

  // resumes (M4, REQ-2.1–2.3)
  listResumes: () => request<{ resumes: Resume[] }>("/v1/resumes"),
  getResume: (id: string) => request<{ resume: Resume }>(`/v1/resumes/${id}`),
  uploadResume: async (file: File): Promise<{ resume: Resume; mode: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    // No JSON content-type: let the browser set the multipart boundary.
    const res = await fetch("/v1/resumes", { method: "POST", credentials: "include", body: fd });
    const text = await res.text();
    const body = text ? (JSON.parse(text) as unknown) : null;
    if (!res.ok) {
      const message =
        body && typeof body === "object" && "error" in body
          ? String((body as { error: unknown }).error)
          : `upload failed (${res.status})`;
      throw new ApiError(res.status, message, body);
    }
    return body as { resume: Resume; mode: string };
  },
  updateResume: (id: string, parsed: ParsedResume) =>
    request<{ resume: Resume }>(`/v1/resumes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(parsed),
    }),
  reparseResume: (id: string) =>
    request<{ resume: Resume; mode: string }>(`/v1/resumes/${id}/reparse`, { method: "POST" }),
  analyzeResume: (id: string) =>
    request<{ created: number; verified: number }>(`/v1/resumes/${id}/analyze`, { method: "POST" }),

  // skills, matching + external verify (M5, REQ-2.4–2.6)
  listSkills: () => request<{ claims: SkillClaim[] }>("/v1/skills"),
  rematchSkills: () => request<{ verified: number }>("/v1/skills/rematch", { method: "POST" }),
  skillRecommendations: (claimId: string) =>
    request<{ recommendations: SkillRecommendation[] }>(`/v1/skills/${claimId}/recommendations`),
  verifyExternal: (url: string) =>
    request<{ result: ExternalVerificationResult }>("/v1/skills/verify-external", {
      method: "POST",
      body: JSON.stringify({ url }),
    }),

  // verified CV builder (M6, REQ-2.7/2.8/2.10)
  listCvs: () => request<{ cvs: CvDocument[] }>("/v1/cv"),
  getCv: (id: string) => request<{ cv: CvDocument }>(`/v1/cv/${id}`),
  createCv: (body: { slug: string; title?: string; resumeId?: string }) =>
    request<{ cv: CvDocument }>("/v1/cv", { method: "POST", body: JSON.stringify(body) }),
  updateCv: (
    id: string,
    patch: {
      title?: string;
      slug?: string;
      sections?: CvSections;
      isSearchableByEmployers?: boolean;
      published?: boolean;
    },
  ) => request<{ cv: CvDocument }>(`/v1/cv/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  requestCvPdf: (id: string) =>
    request<{ status: string }>(`/v1/cv/${id}/pdf`, { method: "POST" }),
  cvPdfUrl: (id: string) => `/v1/cv/${id}/pdf`,

  // account / GDPR-CCPA (M7)
  exportAccountUrl: () => "/v1/account/export",
  deleteAccount: () =>
    request<{ deleted: boolean; summary: Record<string, number>; filesPurged: number }>("/v1/account", {
      method: "DELETE",
    }),
};
